import { useId } from "react";
import { OptimizedImage } from "@/components/OptimizedImage";
import { TRAVELLER_SEATS } from "@/config/site";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { useEnquiry } from "@/context/EnquiryContext";
import { canonicalPath } from "@/lib/canonicalPath";


export const Eyebrow = ({ children, className = "" }) => {
  const { t } = useLanguage();
  return (
  <span className={`inline-block text-xs font-bold uppercase tracking-[0.2em] text-amber-600 ${className}`}>
    {typeof children === "string" ? t(children) : children}
  </span>
  );
};

export const SectionHeading = ({ eyebrow, title, intro, center, light }) => {
  const { t } = useLanguage();
  return (
  <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && <Eyebrow className={light ? "text-amber-400" : ""}>{eyebrow}</Eyebrow>}
    <h2 className={`mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
      {typeof title === "string" ? t(title) : title}
    </h2>
    {intro && (
      <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>
        {typeof intro === "string" ? t(intro) : intro}
      </p>
    )}
  </div>
  );
};

export const QuoteButton = ({ prefill = {}, children = "Request a Quote", className = "", testId = "open-quote-button", ariaLabel }) => {
  const { t } = useLanguage();
  const { openEnquiry } = useEnquiry();
  return (
    <button
      type="button"
      onClick={() => openEnquiry(prefill)}
      data-testid={testId}
      aria-label={ariaLabel}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-base font-bold text-slate-950 shadow-lg transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 ${className}`}
    >
      {typeof children === "string" ? t(children) : children}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};

export const FleetCard = ({ item }) => {
  const { t } = useLanguage();
  const { openEnquiry } = useEnquiry();
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:shadow-xl hover:-translate-y-0.5" data-testid={`fleet-card-${item.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <OptimizedImage
          src={item.image}
          sizes="(min-width: 1024px) 600px, (min-width: 768px) 50vw, calc(100vw - 32px)"
          alt={t(item.alt)}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {item.representative && (
          <span className="absolute bottom-2 left-2 rounded bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
            {t("Representative image")}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-slate-900">{t(item.name)}</h3>
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
            {t(item.category)}
          </span>
        </div>
        <p className="mt-1 text-xs font-medium text-amber-700">{item.id === "tempo-traveller" ? t("{seats} seater options", { seats: TRAVELLER_SEATS.join(", ") }) : item.examples}</p>
        <p className="mt-0.5 text-xs text-slate-500">{t(item.capacityNote)}</p>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(item.bestFor)}</p>
        <ul className="mt-3 grid grid-cols-2 gap-1.5">
          {item.features.map((f) => (
            <li key={f} className="flex items-center gap-1.5 text-xs text-slate-600">
              <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden="true" />
              {t(f)}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => openEnquiry({ vehicle_category: item.name })}
          data-testid={`fleet-request-quote-button-${item.id}`}
          className="mt-5 w-full rounded-full bg-[#0B1E36] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#132D50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
          style={{ marginTop: "auto" }}
        >
          {t("Request a Quote")}
        </button>
      </div>
    </article>
  );
};

export const LocationCard = ({ item }) => {
  const { t, locale, cityName, localizePath, getLocalizedLocation } = useLanguage();
  const localizedItem = getLocalizedLocation(item);
  const note = item.note ? t(item.note) : locale === "hi"
    ? (item.priority === "primary" ? "प्रमुख सेवा क्षेत्र" : "अन्य सेवा क्षेत्र")
    : `${item.priority} service area`;
  return (
  <Link
    to={localizePath(canonicalPath(item.slug))}
    data-testid={`location-card-${item.city.toLowerCase()}`}
    className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm transition-all hover:border-amber-300 hover:shadow-md"
  >
    <div>
      <span className="block font-bold text-slate-900 group-hover:text-amber-700">{localizedItem.displayName || cityName(item.city)}</span>
      <span className="block text-xs text-slate-500 capitalize">{note}</span>
    </div>
    <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-amber-600" aria-hidden="true" />
  </Link>
  );
};

// Native disclosure keeps every answer in initial HTML and works without JavaScript.
export const FaqAccordion = ({ faqs }) => {
  const { t } = useLanguage();
  const group = useId();
  return (
    <div className="w-full" data-testid="faq-accordion">
      {faqs.map((faq, index) => (
        <details key={index} name={group} className="group border-b border-slate-200" data-testid={"faq-item-" + index}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-base font-semibold text-slate-900 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 [&::-webkit-details-marker]:hidden" data-testid={"faq-trigger-" + index}>
            <span>{t(faq.q)}</span><ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
          </summary>
          <div className="pb-4 text-sm leading-relaxed text-slate-600" data-testid={"faq-answer-" + index}>{t(faq.a)}</div>
        </details>
      ))}
    </div>
  );
};

export const Breadcrumbs = ({ items }) => {
  const { t, localizePath } = useLanguage();
  return (
  <nav aria-label={t("Breadcrumb")} className="text-xs text-slate-300">
    <ol className="flex flex-wrap items-center gap-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {it.to ? (
            <Link to={localizePath(canonicalPath(it.to))} className="hover:text-amber-400">{typeof it.label === "string" ? t(it.label) : it.label}</Link>
          ) : (
            <span className="text-amber-400">{typeof it.label === "string" ? t(it.label) : it.label}</span>
          )}
          {i < items.length - 1 && <span aria-hidden="true">/</span>}
        </li>
      ))}
    </ol>
  </nav>
  );
};
